import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/shared/StatusPill";
import { toast } from "sonner";
import {
  StickyNote, CheckSquare, Paperclip, Activity as ActivityIcon, Send, Plus, Clock,
} from "lucide-react";

function timeago(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

export function RecordPanels({ relatedType, relatedId, relatedLabel, sourceApp }) {
  const { has } = useAtlas();
  const rel = { related_type: relatedType, related_id: relatedId, related_label: relatedLabel };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="record-panels">
      <Tabs defaultValue="tasks">
        <TabsList className="w-full justify-start rounded-none border-b border-slate-200 bg-slate-50/60 h-auto p-0">
          <PanelTab value="tasks" icon={CheckSquare} label="Tasks" testid="record-detail-tasks-tab" />
          <PanelTab value="notes" icon={StickyNote} label="Notes" testid="record-detail-notes-tab" />
          <PanelTab value="files" icon={Paperclip} label="Files" testid="record-detail-files-tab" />
          <PanelTab value="activity" icon={ActivityIcon} label="Activity" testid="record-detail-activity-tab" />
        </TabsList>
        <div className="p-4">
          <TabsContent value="notes" className="mt-0"><NotesPanel rel={rel} canEdit={has("notes.manage")} /></TabsContent>
          <TabsContent value="tasks" className="mt-0"><TasksPanel rel={rel} sourceApp={sourceApp} canEdit={has("tasks.manage")} /></TabsContent>
          <TabsContent value="files" className="mt-0"><FilesPanel rel={rel} canEdit={has("files.manage")} /></TabsContent>
          <TabsContent value="activity" className="mt-0"><ActivityPanel relatedType={relatedType} relatedId={relatedId} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function PanelTab({ value, icon: Icon, label, testid }) {
  return (
    <TabsTrigger
      value={value}
      data-testid={testid}
      className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-white data-[state=active]:shadow-none px-4 py-2.5 text-sm gap-2"
    >
      <Icon className="w-4 h-4" /> {label}
    </TabsTrigger>
  );
}

function NotesPanel({ rel, canEdit }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const load = () => api.get("/notes", { params: { related_type: rel.related_type, related_id: rel.related_id } }).then((r) => setNotes(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [rel.related_id]);
  const add = async () => {
    if (!body.trim()) return;
    try { await api.post("/notes", { ...rel, body }); setBody(""); load(); toast.success("Note added"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="flex gap-2 items-end">
          <Textarea data-testid="note-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a note…" className="min-h-[60px]" />
          <Button size="icon" onClick={add} data-testid="note-submit"><Send className="w-4 h-4" /></Button>
        </div>
      )}
      {notes.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No notes yet.</p>}
      {notes.map((n) => (
        <div key={n.id} className="border border-slate-100 rounded-lg p-3 bg-slate-50/50">
          <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.body}</p>
          <p className="text-xs text-slate-400 mt-1.5">{n.author_name || "User"} · {timeago(n.created_at)}</p>
        </div>
      ))}
    </div>
  );
}

function TasksPanel({ rel, sourceApp, canEdit }) {
  const [tasks, setTasks] = useState([]);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("medium");
  const load = () => api.get("/tasks", { params: { related_type: rel.related_type, related_id: rel.related_id } }).then((r) => setTasks(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [rel.related_id]);
  const add = async () => {
    if (!title.trim()) return;
    try {
      await api.post("/tasks", { title, priority, status: "todo", source_app: sourceApp,
        related: [{ type: rel.related_type, id: rel.related_id, label: rel.related_label }] });
      setTitle(""); setAdding(false); load(); toast.success("Task created");
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (t) => {
    try {
      await api.put(`/tasks/${t.id}`, { ...t, status: t.status === "done" ? "todo" : "done" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-2">
      {canEdit && !adding && (
        <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="task-add-toggle"><Plus className="w-4 h-4 mr-1" /> Add task</Button>
      )}
      {adding && (
        <div className="flex gap-2 items-center border border-slate-200 rounded-lg p-2">
          <Input data-testid="task-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title" className="flex-1" />
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="w-28" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={add} data-testid="task-submit">Save</Button>
        </div>
      )}
      {tasks.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No tasks yet.</p>}
      {tasks.map((t) => (
        <div key={t.id} className="flex items-center gap-3 border border-slate-100 rounded-lg p-2.5" data-testid={`task-row-${t.id}`}>
          <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} disabled={!canEdit} className="w-4 h-4 accent-blue-600" data-testid={`task-check-${t.id}`} />
          <span className={`flex-1 text-sm ${t.status === "done" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</span>
          <StatusPill value={t.priority} />
        </div>
      ))}
    </div>
  );
}

function FilesPanel({ rel, canEdit }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/files", { params: { related_type: rel.related_type, related_id: rel.related_id } }).then((r) => setFiles(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [rel.related_id]);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("related_type", rel.related_type); fd.append("related_id", rel.related_id); if (rel.related_label) fd.append("related_label", rel.related_label);
    setBusy(true);
    try { await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File uploaded"); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const download = async (f) => {
    try { const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" }); const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = f.name; a.click(); setTimeout(() => URL.revokeObjectURL(url), 5000); }
    catch (err) { toast.error(apiError(err, "Download failed")); }
  };
  const remove = async (f) => { try { await api.delete(`/files/${f.id}`); toast.success("File removed"); load(); } catch (err) { toast.error(apiError(err)); } };
  const fmt = (n) => n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`;
  return (
    <div className="space-y-2">
      {canEdit && <label className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border cursor-pointer hover:bg-slate-50" data-testid="file-upload-label"><Plus className="w-4 h-4" /> {busy ? "Uploading…" : "Upload file"}<input type="file" className="hidden" onChange={upload} disabled={busy} data-testid="file-upload-input" /></label>}
      {files.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No files attached.</p>}
      {files.map((f) => (
        <div key={f.id} className="flex items-center gap-3 border border-slate-100 rounded-lg p-2.5 hover:bg-slate-50" data-testid="file-row">
          <Paperclip className="w-4 h-4 text-slate-400" />
          <button onClick={() => download(f)} className="flex-1 text-left text-sm text-slate-800 hover:underline truncate" data-testid="file-download">{f.name}</button>
          <span className="text-xs text-slate-400 whitespace-nowrap">{fmt(f.size)} · {f.uploaded_by_name || "—"} · {timeago(f.created_at)}</span>
          {canEdit && <button onClick={() => remove(f)} className="text-xs text-slate-400 hover:text-red-600" data-testid="file-delete">Remove</button>}
        </div>
      ))}
    </div>
  );
}

function ActivityPanel({ relatedType, relatedId }) {
  const [items, setItems] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/activity", { params: { related_type: relatedType, related_id: relatedId } }).then((r) => setItems(r.data)); }, [relatedId]);
  return (
    <div className="space-y-0">
      {items.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No activity yet.</p>}
      {items.map((a, i) => (
        <div key={a.id} className="flex gap-3 pb-4 relative">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
            {i < items.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
          </div>
          <div className="pb-1">
            <p className="text-sm text-slate-800">{a.summary}</p>
            <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" />{a.actor_name || "System"} · {timeago(a.created_at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
